import { Link, useNavigate } from 'react-router-dom';
import { TableAction, TableColumn, Sport } from '@/types';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import { useSports, useDeleteSport, useTranslation, useConfirmDialog } from '@/hooks';
import toast from 'react-hot-toast';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Button from '@/components/ui/Button';

const Sports = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();

  const deleteSportMutation = useDeleteSport();
  const sportsColumns: TableColumn<Sport>[] = [
    {
      key: 'title',
      header: t.sports.fields.title,
      accessor: (sport) => (
        <div className="font-medium text-gray-900">
          <Link
            className="link"
            to={`/content/sports/${sport.id}/edit`}>{sport.title}</Link>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      key: 'year',
      header: t.sports.fields.year,
      accessor: (sport) => (
        <div className="text-gray-700">
          {sport.year}
        </div>
      )
    },
    {
      key: 'genres',
      header: t.sports.fields.genres,
      accessor: (sport) => (
        <div className="flex flex-wrap gap-1">
          {sport.genres?.slice(0, 2).map((genre) => (
            <span key={genre} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {t.sports.genres[genre] || genre}
            </span>
          ))}
          {sport.genres && sport.genres.length > 2 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              +{sport.genres.length - 2}
            </span>
          )}
        </div>
      ),
      className: 'min-w-32'
    },
    {
      sortable: true,
      key: 'views',
      header: 'Visitas',
      accessor: (sport) => (
        <div className="text-gray-700">
          {sport.views?.toLocaleString() || 0}
        </div>
      )
    },
    {
      key: 'updatedDate',
      sortable: true,
      header: t.common.fields.updatedDate,
      accessor: (sport) => (
        <div className="text-gray-500">
          {sport.updatedDate ? new Date(sport.updatedDate).toLocaleDateString('es'):'N/D'}
        </div>
      ),
    }
  ];

  const handleDelete = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: t.sports.messages.confirmDelete,
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteSportMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
    } catch (error) {
      console.error('Error deleting sport:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      return;
    }

    toast.success('Deporte eliminado exitosamente');
  };

  const sportsActions: TableAction<Sport>[] = [
    {
      label: t.sports.actions.edit,
      icon: Edit,
      onClick: (sport) => navigate(`/content/sports/${sport.id}/edit`),
      className: 'text-blue-600 hover:text-blue-800'
    },
    {
      label: t.sports.actions.delete,
      icon: Trash2,
      onClick: (sport) => handleDelete(sport.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handleCreateSport = () => {
    navigate('/content/sports/create');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <DataTableWithQuery<Sport>
          showTotalCount
          tableStyle="compact"
          useQueryHook={useSports}
          columns={sportsColumns}
          actions={sportsActions}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{t.sports.listTitle}</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <Button onClick={handleCreateSport} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo deporte
                  </Button>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">{t.sports.listTitle}</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-auto">
                  <Button onClick={handleCreateSport} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Nuevo deporte
                  </Button>
                </div>
              </div>
            </div>
          )}
          onCreate={handleCreateSport}
          title={t.sports.listTitle}
          itemsPerPage={25}
          searchPlaceholder={t.sports.placeholders.search}
        />
        {dialog}
      </div>
    </div>
  )
};

export default Sports;
